import type { DocumentState } from "./types";

export type PostUploadContext = {
  docTypeFriendly: string;
  fillCount: number;
  totalAreas: number;
  suggestedFriendly: string | null;
  suggestedDescription: string | null;
  isJd: boolean;
  hasJd: boolean;
  allFilled: boolean;
};

export const CATEGORY_FRIENDLY_NAMES: Record<string, string> = {
  JOB_DESCRIPTION: "job description",
  CURRENT_INTERVIEW_PROCESS: "interview process materials",
  IDEAL_CANDIDATE_PROFILE: "ideal candidate profile",
  CULTURE_AND_TEAM_CONTEXT: "culture and team context",
  PAST_INTERVIEW_MATERIAL: "recordings",
};

const CATEGORY_SUGGESTIONS: Record<string, string> = {
  CURRENT_INTERVIEW_PROCESS:
    "interview guides, scorecards, or evaluation rubrics",
  IDEAL_CANDIDATE_PROFILE:
    "ideal candidate docs, intake notes, or leveling expectations",
  CULTURE_AND_TEAM_CONTEXT:
    "company values docs, culture decks, or team context",
  PAST_INTERVIEW_MATERIAL:
    "past interview recordings, intake meetings, or debrief notes",
};

const SUGGESTION_PRIORITY = [
  "CURRENT_INTERVIEW_PROCESS",
  "IDEAL_CANDIDATE_PROFILE",
  "CULTURE_AND_TEAM_CONTEXT",
  "PAST_INTERVIEW_MATERIAL",
] as const;

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function pickVariant(
  variants: string[],
  usedIndices: Set<number>
): string {
  const available = variants
    .map((_, i) => i)
    .filter((i) => !usedIndices.has(i));
  if (available.length === 0) {
    usedIndices.clear();
    const idx = Math.floor(Math.random() * variants.length);
    usedIndices.add(idx);
    return variants[idx];
  }
  const idx = pickRandom(available);
  usedIndices.add(idx);
  return variants[idx];
}

function getGreetingVariants(displayName: string): string[] {
  return [
    `Hello! Let's start building your AI interviewer for the **${displayName}** role. Start by sharing a job description — you can drag & drop a file right into this chat, or use the options below to upload, paste, or link one.`,
    `Welcome! I'm ready to help set up your **${displayName}** interviewer. To get started, I'll need a job description. You can drag & drop a file anywhere here, or use any of the input options below.`,
    `Hi there! Let's get your **${displayName}** AI interviewer set up. The first thing I need is a job description — drag & drop a file right here, or use the options below to upload, paste, or link one.`,
    `Great to meet you! To build a strong interview guide for **${displayName}**, I'll need the job description. Feel free to drag & drop it right into this window, or choose any method below to share it.`,
    `Hey! Let's train your AI interviewer for **${displayName}**. First up: the job description. Drag & drop a file directly into this chat, or use the options below.`,
    `Ready to build your **${displayName}** interviewer! Share a job description to get started — drag & drop a file right here, or use the buttons below to upload, paste, or add a link.`,
    `Let's create an excellent interview experience for your **${displayName}** role. Share a job description to kick things off — you can drag & drop a file directly into this chat, or use the options below.`,
    `Hi! I'll help you set up a tailored AI interviewer for **${displayName}**. Start by adding a job description — drag & drop a file anywhere here, or use the input options below.`,
    `Welcome! To get your **${displayName}** interviewer off the ground, I just need a job description. Drag & drop a file right into this chat, or upload, paste, or link one using the options below.`,
    `Hello! I'm here to help build your **${displayName}** interview guide. Let's begin — you can drag & drop a file directly here, or use any of the input methods below.`,
    `Let's do this! Share the job description for **${displayName}** and we'll start building your interview guide. Drag & drop a file right here, or use the options below.`,
    `Hi! Ready to create a great interviewer for your **${displayName}** role. The first step is simple — drag & drop a file into this chat, or use the buttons below to share it.`,
  ];
}

function getPostJdVariants(ctx: PostUploadContext): string[] {
  const { suggestedFriendly, suggestedDescription } = ctx;
  const nextHint =
    suggestedFriendly && suggestedDescription
      ? ` If you have any **${suggestedFriendly}** like ${suggestedDescription}, those would help shape a stronger guide.`
      : "";

  return [
    `Got your **job description** — that's the foundation! You can **generate your interview guide** now, or keep adding materials to make it even stronger.${nextHint}`,
    `**Job description** received! That's the key piece. You're ready to **generate** whenever you'd like, or you can add more context to level up the results.${nextHint}`,
    `Great — I've got your **job description**. You can go ahead and **generate** your guide now, or add more materials for a more tailored experience.${nextHint}`,
    `Your **job description** is in. That's the most important piece! Hit **Generate** when you're ready, or share more materials to give the interviewer even more to work with.${nextHint}`,
    `**Job description** locked in! You can **generate** right now, or take a moment to add more context that'll help the AI ask better questions.${nextHint}`,
    `Nice — **job description** received. You're all set to **generate**, or you can keep going by adding more interview materials.${nextHint}`,
    `I've got your **job description** — solid start! You can **generate your guide** anytime, or share additional materials to make it richer.${nextHint}`,
    `**Job description** is in — we're off to a great start. Feel free to **generate** now, or add more materials to help the interviewer really understand the role.${nextHint}`,
    `Your **job description** is ready to go. You can **generate** your interview guide now, or keep adding materials for an even more thorough result.${nextHint}`,
    `Got it — **job description** received! Ready to **generate** whenever you are. Or, if you've got more context to share, adding it now will make the guide even better.${nextHint}`,
    `**Job description** captured. You can **generate** immediately, or strengthen the output by sharing additional context about the role.${nextHint}`,
    `The **job description** is in place. You're ready to **generate** — or if you have more materials, adding them now will produce a more comprehensive guide.${nextHint}`,
  ];
}

function getEncouragement(fillCount: number): string {
  if (fillCount >= 4) {
    return pickRandom([
      "Almost there!",
      "You're so close to full coverage!",
      "Nearly complete — impressive!",
      "Just one more area would max it out!",
      "The finish line is in sight!",
    ]);
  }
  if (fillCount >= 3) {
    return pickRandom([
      "Great progress!",
      "Looking really good!",
      "The guide is shaping up nicely.",
      "This is going to make a strong guide.",
      "Nice momentum!",
    ]);
  }
  return pickRandom([
    "Good addition!",
    "That'll help!",
    "Nice — every bit of context counts.",
    "Building a solid foundation!",
    "The guide keeps getting stronger.",
  ]);
}

function getPostDocVariants(ctx: PostUploadContext): string[] {
  const {
    docTypeFriendly,
    fillCount,
    totalAreas,
    hasJd,
    suggestedFriendly,
    suggestedDescription,
  } = ctx;

  const progress = `**${fillCount} of ${totalAreas}**`;
  const nextHint =
    suggestedFriendly && suggestedDescription
      ? ` If you have **${suggestedFriendly}** like ${suggestedDescription}, that would be a great addition.`
      : "";
  const encouragement = getEncouragement(fillCount);

  if (!hasJd) {
    return [
      `**${docTypeFriendly}** received! ${encouragement} You're at ${progress} context areas. Keep adding context, then upload a **job description** when you're ready to generate your interview guide.${nextHint}`,
      `Got your **${docTypeFriendly}**. ${encouragement} That's ${progress} areas covered. Add a **job description** to unlock guide generation.${nextHint}`,
      `**${docTypeFriendly}** added — nice! You're at ${progress} context areas now. Feel free to add more context. A **job description** is the key piece I need to generate your guide.${nextHint}`,
      `Nice, **${docTypeFriendly}** is in! ${encouragement} ${progress} areas filled. Once you add a **job description**, you'll be able to generate your guide.${nextHint}`,
      `I've processed your **${docTypeFriendly}**. ${encouragement} You're at ${progress} areas. Share a **job description** and you'll be ready to generate your guide.${nextHint}`,
      `**${docTypeFriendly}** locked in! ${encouragement} That brings you to ${progress} context areas. Add a **job description** to get started with guide generation.${nextHint}`,
    ];
  }

  return [
    `**${docTypeFriendly}** received! ${encouragement} You're at ${progress} context areas. You can **generate** anytime, or keep adding.${nextHint}`,
    `Got your **${docTypeFriendly}**. ${encouragement} That's ${progress} areas covered. Ready to **generate**, or feel free to add more.${nextHint}`,
    `**${docTypeFriendly}** added — nice! You're at ${progress} context areas now. **Generate** whenever you're ready, or keep going.${nextHint}`,
    `Nice, **${docTypeFriendly}** is in! ${encouragement} ${progress} areas filled. Hit **Generate** when you'd like, or add more for extra depth.${nextHint}`,
    `I've processed your **${docTypeFriendly}**. ${encouragement} You're at ${progress} areas. You can **generate** your guide now, or share more materials.${nextHint}`,
    `**${docTypeFriendly}** locked in! ${encouragement} That brings you to ${progress} context areas. **Generate** anytime, or keep adding.${nextHint}`,
    `Your **${docTypeFriendly}** is ready. ${encouragement} ${progress} areas covered. You can **generate** now, or add more context to strengthen the guide.${nextHint}`,
    `**${docTypeFriendly}** captured. ${encouragement} You're up to ${progress} context areas. Feel free to **generate**, or share more materials.${nextHint}`,
    `Got it — **${docTypeFriendly}** processed! ${encouragement} ${progress} areas done. **Generate** whenever you're ready, or there's room for more.${nextHint}`,
    `**${docTypeFriendly}** is in the mix. ${encouragement} You've covered ${progress} areas. **Generate** now, or add more materials to dial it in.${nextHint}`,
    `Received your **${docTypeFriendly}**! ${encouragement} Sitting at ${progress} areas. You can **generate** anytime, or keep building up the context.${nextHint}`,
    `**${docTypeFriendly}** added to the mix! ${encouragement} That puts you at ${progress} context areas. Ready to **generate**, or add more if you've got it.${nextHint}`,
  ];
}

function getAllFilledVariants(): string[] {
  return [
    "You've covered all **5 context areas** — that's incredible! Your AI interviewer is going to be really well-trained. I'd highly recommend hitting **Generate** now to create your interview guide.",
    "All **5 areas** are filled — amazing work! This is the best possible foundation for your interview guide. Go ahead and hit **Generate** — the results are going to be excellent.",
    "**5 out of 5** context areas — you've gone above and beyond! Your interviewer will have a deep understanding of the role. Hit **Generate** to see your tailored interview guide!",
    "Every context area is covered! That's the gold standard. **Generate** your guide now and see how all that context translates into a thorough interview experience.",
    "All **5 context areas** are in — your AI interviewer has everything it needs. **Generate** your interview guide now for the best possible results!",
    "Wow, all **5 areas** filled! This is going to produce an exceptional guide. I'd strongly recommend hitting **Generate** now — you've given the AI everything it needs to succeed.",
    "Full coverage — all **5 context areas** complete! Your interview guide is going to be thorough and well-tailored. **Generate** whenever you're ready — and you're definitely ready.",
    "You've filled every context area. That's rare and impressive! **Generate** your guide now to see just how comprehensive the output can be.",
    "**5 of 5** — maximum context provided! Your AI interviewer is set up for success. Go ahead and hit **Generate** — the guide will be tailored to exactly what you need.",
    "All context areas covered — nicely done! The more context, the better the guide. You're at the sweet spot. Hit **Generate** to bring it all together!",
    "Every single context area filled! You're set up for the best possible interview guide. Ready to **generate**? I'd say now's the perfect time.",
    "All **5 areas** covered — your AI interviewer will have a comprehensive understanding of the role. **Generate** your guide now, and it'll reflect all the depth you've provided.",
  ];
}

export function pickGreeting(
  displayName: string,
  usedIndices: Set<number>
): string {
  return pickVariant(getGreetingVariants(displayName), usedIndices);
}

export function buildPostUploadMessage(
  ctx: PostUploadContext,
  usedIndices: Set<number>
): string {
  if (ctx.allFilled) {
    return pickVariant(getAllFilledVariants(), usedIndices);
  }
  if (ctx.isJd) {
    return pickVariant(getPostJdVariants(ctx), usedIndices);
  }
  return pickVariant(getPostDocVariants(ctx), usedIndices);
}

export function getNextSuggestion(
  documentState: DocumentState,
  jobDescriptionProvided: boolean
): { category: string; friendly: string; description: string } | null {
  if (!jobDescriptionProvided) return null;

  const stateMap: Record<string, boolean> = {
    CURRENT_INTERVIEW_PROCESS: documentState.currentInterviewProcess,
    IDEAL_CANDIDATE_PROFILE: documentState.idealCandidateProfile,
    CULTURE_AND_TEAM_CONTEXT: documentState.cultureTeamContext,
    PAST_INTERVIEW_MATERIAL: documentState.pastInterviewTranscripts,
  };

  const cat = SUGGESTION_PRIORITY.find((c) => !stateMap[c]);
  if (!cat) return null;

  return {
    category: cat,
    friendly: CATEGORY_FRIENDLY_NAMES[cat],
    description: CATEGORY_SUGGESTIONS[cat],
  };
}
